import React, { useState, useContext, useEffect, createContext, useCallback } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import api from '../api/api';
import { AuthContext } from './AuthContext';

export const ChatSessionContext = createContext();

// Custom hook to easily access the message handler context
export const useChatSession = () => {
    return useContext(ChatSessionContext);
};

// Provider component for the chat session context
export const ChatSessionProvider = ({ children }) => {
    const [isInitialized, setIsInitialized] = useState(false);
    const [chatSessions, setChatSessions] = useState([]);
    const [groupedChatSessions, setGroupedChatSessions] = useState([]);
    const [projects, setProjects] = useState([]);
    const [expandedProjects, setExpandedProjects] = useState({});
    const { isAuthenticated, userType } = useContext(AuthContext);
    const { t } = useTranslation();

    const isRegisteredUser = isAuthenticated && userType === 'registered';

    const fetchProjects = useCallback(async () => {
        try {
            if (!isRegisteredUser) return;

            const response = await api.get(`${process.env.REACT_APP_API_BASE_URL}/api/chat/projects`);
            setProjects(response.data.projects || []);
        } catch (error) {
            console.error('Error fetching projects:', error);
        }
    }, [isRegisteredUser]);

    const createProject = async (name, color, fontColor = 'default', contextEnabled = false) => {
        try {
            const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/api/chat/projects`, {
                name,
                color,
                fontColor,
                contextEnabled
            });

            if (response.data.success) {
                await fetchProjects();
                return response.data.project.id;
            }
        } catch (error) {
            console.error('Error creating project:', error);
        }
    };

    const createChatInProject = async (projectId, chatName = null) => {
        try {
            const response = await api.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/chat/projects/${projectId}/sessions/new`,
                { sessionName: chatName }
            );

            if (response.data.success) {
                await fetchChatSessions();
                return response.data.session;
            }
        } catch (error) {
            console.error('Error creating chat in project:', error);
            throw error;
        }
    };

    const toggleProjectContext = async (projectId, enabled) => {
        try {
            const response = await api.put(
                `${process.env.REACT_APP_API_BASE_URL}/api/chat/projects/${projectId}/context`,
                { enabled }
            );

            if (response.data.success) {
                await fetchProjects();
                return true;
            }
        } catch (error) {
            console.error('Error toggling project context:', error);
            return false;
        }
    };

    const updateProject = async (projectId, updates) => {
        try {
            const response = await api.put(`${process.env.REACT_APP_API_BASE_URL}/api/chat/projects/${projectId}`, updates);

            if (response.data.success) {
                await fetchProjects();
                return true;
            }
        } catch (error) {
            console.error('Error updating project:', error);
        }
        return false;
    };

    const deleteProject = async (projectId) => {
        try {
            const response = await api.delete(`${process.env.REACT_APP_API_BASE_URL}/api/chat/projects/${projectId}`);

            if (response.data.success) {
                await fetchProjects();
                return true;
            }
        } catch (error) {
            console.error('Error deleting project:', error);
        }
        return false;
    };

    const moveSessionToProject = async (sessionId, projectId) => {
        try {
            if (projectId === null) {
                // Find which project contains this session
                const projectWithSession = projects.find(
                    project => project.sessions.includes(sessionId)
                );

                if (projectWithSession) {
                    // Remove from project
                    await api.delete(
                        `${process.env.REACT_APP_API_BASE_URL}/api/chat/projects/${projectWithSession.id}/sessions/${sessionId}`
                    );
                }
            } else {
                // Add to specified project
                await api.post(
                    `${process.env.REACT_APP_API_BASE_URL}/api/chat/projects/${projectId}/sessions`,
                    { sessionId }
                );

            }

            // Refresh the lists
            await fetchChatSessions();
            await fetchProjects();

        } catch (error) {
            console.error('Error moving session to project:', error);
        }
        return false;
    };

    const updateProjectColor = async (projectId, color) => {
        return await updateProject(projectId, { color });
    };

    const toggleProjectExpanded = (projectId) => {
        setExpandedProjects(prev => ({
            ...prev,
            [projectId]: !prev[projectId]
        }));
    };

    // Initialize projects when component mounts
    useEffect(() => {
        fetchProjects();
    }, [fetchProjects]);

    const fetchChatSessions = useCallback(async () => {
        try {
            const response = await api.get(`${process.env.REACT_APP_API_BASE_URL}/api/chat/sessions`);
            
            setChatSessions(response.data.chatSessions);
            setGroupedChatSessions(groupChatSessions(response.data.chatSessions))
        } catch (error) {
            console.error('Error fetching chat sessions:', error);
        }
    }, []);

    useEffect(() => {
        const initializeApp = async () => {
            try {
                if (isRegisteredUser) {
                    await fetchChatSessions();
                    setIsInitialized(true);
                }
            } catch (error) {
                console.error('Error initializing app:', error);
            }
        };

        if (!isInitialized && isRegisteredUser) {
            initializeApp();
        }
    }, [isInitialized, isRegisteredUser, fetchChatSessions]);

    useEffect(() => {
        if (isRegisteredUser) {
            setGroupedChatSessions(groupChatSessions(chatSessions));
        }
    }, [chatSessions, isRegisteredUser]);

    const updateChatSessions = async () => {
        try {
            const response = await api.get(`${process.env.REACT_APP_API_BASE_URL}/api/chat/sessions`);
            setChatSessions(response.data.chatSessions);
            return response.data.chatSessions
        } catch (error) {
            console.error('Error updating chat sessions:', error);
        }
    };

    // Handle loading a chat session
    const loadChatSession = async (sessionId) => {
        try {
            const response = await api.get(`${process.env.REACT_APP_API_BASE_URL}/api/chat/sessions/${sessionId}`);
            return {
                conversationHistory: response.data.conversationHistory,
                engine: response.data.engine
            }
        } catch (error) {
            console.error('Error loading chat session:', error);
        }
    };

    // Handle starting a new chat
    const newChatSession = async () => {
        try {
            const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/api/chat/reset`);
            return response.data.conversationHistory
        } catch (error) {
            console.error('Error starting new chat:', error);
        }
    };

    // Function to group chat sessions by date
    const groupChatSessions = (chatSessionsList) => {
        if (!Array.isArray(chatSessionsList)) {
            return { today: [], yesterday: [], last7Days: [], last30Days: [], months: {}, years: {} };
        }

        const today = [];
        const yesterday = [];
        const last7Days = [];
        const last30Days = [];
        const months = {};
        const years = {};

        const now = moment();

        chatSessionsList.forEach(session => {
            const updatedAt = moment(session.updatedAt);

            if (updatedAt.isSame(now, 'day')) {
                today.push(session);
            } else if (updatedAt.isAfter(now.clone().subtract(1, 'days'))) {
                yesterday.push(session);
            } else if (updatedAt.isAfter(now.clone().subtract(7, 'days'))) {
                last7Days.push(session);
            } else if (updatedAt.isAfter(now.clone().subtract(30, 'days'))) {
                last30Days.push(session);
            } else if (updatedAt.isSame(now, 'year')) {
                const month = updatedAt.format('MMMM');
                if (!months[month]) {
                    months[month] = [];
                }
                months[month].push(session);
            } else {
                const year = updatedAt.format('YYYY');
                if (!years[year]) {
                    years[year] = [];
                }
                years[year].push(session);
            }
        });

        // Sort each group in descending order by updatedAt
        today.sort((a, b) => moment(b.updatedAt) - moment(a.updatedAt));
        yesterday.sort((a, b) => moment(b.updatedAt) - moment(a.updatedAt));
        last7Days.sort((a, b) => moment(b.updatedAt) - moment(a.updatedAt));
        last30Days.sort((a, b) => moment(b.updatedAt) - moment(a.updatedAt));
        Object.keys(months).forEach(month => {
            months[month].sort((a, b) => moment(b.updatedAt) - moment(a.updatedAt));
        });
        Object.keys(years).forEach(year => {
            years[year].sort((a, b) => moment(b.updatedAt) - moment(a.updatedAt));
        });

        return { today, yesterday, last7Days, last30Days, months, years };
    };

    const groupName = (group) => {
        // Check if the group key is one of our time groups
        if (['today', 'yesterday', 'last7Days', 'last30Days', 'months', 'years'].includes(group)) {
            return t(`timeGroups.${group}`);
        }
        return t(group);
    }

    return (
        <ChatSessionContext.Provider
            value={{
                chatSessions,
                setChatSessions,
                fetchChatSessions,
                updateChatSessions,
                loadChatSession,
                newChatSession,
                groupChatSessions,
                groupedChatSessions,
                groupName,
                projects,
                expandedProjects,
                createProject,
                deleteProject,
                moveSessionToProject,
                updateProjectColor,
                toggleProjectExpanded,
                fetchProjects,
                createChatInProject,
                toggleProjectContext
            }}
        >
            {children}
        </ChatSessionContext.Provider>
    );
}